<template>
    <div class="refinancing-block">
        <h3>Refinancing results</h3>
        <ul class="refinance-graph">
            <li>
                <h4>Your estimated savings over the life of the loan</h4>
                <span class="save-amt" :class="className(0, InterestCostResult)">{{ EstimatedSavings }}<span>/mo</span></span>
                <button class="btn btn-blue" type="submit" @click="redirect()">{{ DestinationButtonText }}</button>
                <div>
                    <advertising-disclosures
                        v-if="disclosureText"
                        :disclosure-text="disclosureText"
                        :disclosure-tokens="disclosureTokens"
                    />
                </div>
                <lt-logo v-if="ltLogo == 1">
                    <small>NMLS#1136 Terms & Conditions Apply</small>
                </lt-logo>
            </li>
        </ul>

        <ul class="graph-payment-block">
            <li>
                <div class="amt-block">
                    <label class="title">Monthly Payment</label>
                    <div class="amt-val">
                        <span>${{ currencyFilter(monthlyPayment) }}</span>
                        <small>vs</small>
                        <b :class="className(RefiLoanMonthlyPayment, monthlyPayment)">${{ currencyFilter(RefiLoanMonthlyPayment) }}</b>
                    </div>
                    <div class="up-down-val">
                        <i v-if="PaymentArrowDirection !== null" :class="{'lt4-ArrowDownCircle': PaymentArrowDirection, 'lt4-ArrowUpCircle' : !PaymentArrowDirection}"></i>
                        <span>Refinancing
                            <span>{{ PaymentResultComp }}</span> your monthly payments
                            <span>{{ PaymentResultBy }}</span>
                            <span>{{ PaymentResultAmount }}</span>
                        </span>
                    </div>
                </div>
            </li>
            <li>
                <div class="amt-block">
                    <label class="title">Remaining Months</label>
                    <div class="amt-val">
                        <span>{{ MonthsLeftOnCurrentLoan }} Mo.</span>
                        <small>vs</small>
                        <b :class="className(NumberOfMonthsOnRefiLoan, MonthsLeftOnCurrentLoan)">{{ NumberOfMonthsOnRefiLoan }} Mo.</b>
                    </div>
                    <div class="up-down-val">
                        <i v-if="MonthsArrowDirection != null" :class="{'lt4-ArrowDownCircle': MonthsArrowDirection, 'lt4-ArrowUpCircle' : !MonthsArrowDirection}"></i>
                        <span>Refinancing
                            <span>{{ MonthsResultComp }}</span> your loan term
                            <span>{{ MonthsResultBy }}</span>
                            <span>{{ MonthsResultAmount }}</span>
                        </span>
                    </div>
                </div>
            </li>
            <li>
                <div class="amt-block">
                    <label class="title">Remaining Interest
                        <lt-label :tooltip-link="tooltipRemainingInterest" />
                    </label>
                    <div class="amt-val">
                        <span>${{ currencyFilter(RemainingInterestOnCurrentLoan) }}</span>
                        <small>vs</small>
                        <b :class="className(Remaininginterestonrefiloan, RemainingInterestOnCurrentLoan)">
                            <span v-if="Remaininginterestonrefiloan < 0">(</span>${{ currencyFilter(Math.abs(Remaininginterestonrefiloan)) }}<span v-if="Remaininginterestonrefiloan < 0">)</span>
                        </b>
                    </div>
                    <div class="up-down-val">
                        <i v-if="InterestArrowDirection != null" :class="{'lt4-ArrowDownCircle': InterestArrowDirection, 'lt4-ArrowUpCircle' : !InterestArrowDirection}"></i>
                        <span>Refinancing
                            <span>{{ InterestResultComp }}</span> your interest cost
                            <span>{{ InterestResultBy }}</span>
                            <span>{{ InterestResultAmount }}</span>
                        </span>
                    </div>
                </div>
            </li>
        </ul>
    </div>
</template>

<script>
    import Accounting from 'accounting';
    import LtLogo from '~/components/lt-logo.vue';
    import LtLabel from '~/components/lt-label.vue';
    import AdvertisingDisclosures from '~/components/advertising-disclosures.vue';
    import BaseMixin from '~/mixin/base-mixin';
    import PaymentHelper from '~/helpers/payment';

    export default {
        props: {
            savingSection: {
                default: '',
            },
            planStayYears: {
                default: 12,
            },
            disclosureTokens: {
                default: '',
            },
            disclosureText: {
                default: '',
            },
            ltLogo: {
                default: 0,
            },
            tooltipRemainingInterest: {
                default: 'remaining-interest',
            },
            destinationLinkOrId: {
                default: '',
            },
            destinationButtonText: {
                default: '',
            },
            currentBalance: {
                default: 0,
            },
            monthlyPayment: {
                default: 0,
            },
            currentInterestRate: {
                default: 0,
            },
            loanAmount: {
                default: 0,
            },
            loanTermInYears: {
                default: 0,
            },
            refinanceInterestRate: {
                default: 0,
            },
        },
        data() {
            return {
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
                MonthsLeftOnCurrentLoan: '',
                RemainingInterestOnCurrentLoan: '',
                RefiLoanMonthlyPayment: '',
                NumberOfMonthsOnRefiLoan: '',
                Remaininginterestonrefiloan: '',
                LoanTermResult: '',
                InterestCostResult: '',
                PaymentResultComp: '',
                PaymentResultBy: '',
                PaymentResultAmount: '',
                PaymentArrowDirection: false,
                MonthsResultComp: '',
                MonthsResultBy: '',
                MonthsResultAmount: '',
                MonthsArrowDirection: false,
                InterestResultComp: '',
                InterestResultBy: '',
                InterestResultAmount: '',
                InterestArrowDirection: false,
                EstimatedSavings: '',
            };
        },
        methods: {
            calculateRefinance() {
                this.MonthsLeftOnCurrentLoan = PaymentHelper.getMonthsLeft(this.currentInterestRate, this.unformatFigure(this.currentBalance), this.unformatFigure(this.monthlyPayment));
                this.RemainingInterestOnCurrentLoan = this.unformatFigure(this.monthlyPayment) * this.MonthsLeftOnCurrentLoan - this.unformatFigure(this.currentBalance);
                this.RemainingInterestOnCurrentLoan = this.RemainingInterestOnCurrentLoan < 0 ? 0 : this.RemainingInterestOnCurrentLoan;
                this.NumberOfMonthsOnRefiLoan = this.loanTermInYears * 12;
                this.RefiLoanMonthlyPayment = Math.round(PaymentHelper.monthlyPaymentForRefinance(this.MonthsLeftOnCurrentLoan, this.unformatFigure(this.loanAmount), this.unformatFigure(this.monthlyPayment), this.loanTermInYears, this.refinanceInterestRate));
                this.Remaininginterestonrefiloan = this.RefiLoanMonthlyPayment * this.NumberOfMonthsOnRefiLoan - this.unformatFigure(this.loanAmount);
                this.MonthlyPaymentResult = this.unformatFigure(this.monthlyPayment) - this.RefiLoanMonthlyPayment;
                this.LoanTermResult = this.NumberOfMonthsOnRefiLoan - this.MonthsLeftOnCurrentLoan;
                this.InterestCostResult = this.RemainingInterestOnCurrentLoan - this.Remaininginterestonrefiloan;

                // Monthly Payment
                // eslint-disable-next-line eqeqeq
                if (this.MonthlyPaymentResult == 0) {
                    this.PaymentResultComp = 'does not change';
                    this.PaymentResultAmount = '';
                    this.PaymentResultBy = '. Try changing your loan term.';
                    this.PaymentArrowDirection = null;
                } else {
                    this.PaymentResultComp = this.MonthlyPaymentResult > 0 ? 'Decreases ' : 'Increases ';
                    this.PaymentResultAmount = Accounting.formatMoney(Math.abs(this.MonthlyPaymentResult), { precision: 0 });
                    this.PaymentResultBy = 'by';
                    this.PaymentArrowDirection = this.MonthlyPaymentResult > 0;
                }

                // Remaining Months
                // eslint-disable-next-line eqeqeq
                if (this.LoanTermResult == 0) {
                    this.MonthsResultComp = 'does not change';
                    this.MonthsResultAmount = '';
                    this.MonthsResultBy = '.';
                    this.MonthsArrowDirection = null;
                } else {
                    this.MonthsResultComp = this.LoanTermResult < 0 ? 'Decreases ' : 'Increases ';
                    this.MonthsResultAmount = `${Accounting.formatMoney(Math.abs(this.LoanTermResult), { symbol: '', precision: 0 })} months`;
                    this.MonthsResultBy = ' by ';
                    this.MonthsArrowDirection = this.LoanTermResult < 0;
                }

                // Remaining Interest
                // eslint-disable-next-line eqeqeq
                if (this.InterestCostResult == 0) {
                    this.InterestResultComp = 'does not change';
                    this.InterestResultAmount = '';
                    this.InterestResultBy = '.';
                    this.InterestArrowDirection = null;
                    this.EstimatedSavings = Accounting.formatMoney(0, { precision: 0 });
                } else {
                    this.InterestResultComp = this.InterestCostResult > 0 ? 'Decreases ' : 'Increases ';
                    this.InterestResultAmount = Accounting.formatMoney(Math.abs(this.InterestCostResult), { precision: 0 });
                    this.InterestResultBy = ' over your auto loan by ';
                    this.InterestArrowDirection = this.InterestCostResult > 0;
                    this.EstimatedSavings = this.InterestResultAmount;
                }
            },
            className(firstValue, secondValue) {
                return firstValue < secondValue
                    ? 'green-text'
                    : firstValue > secondValue
                        ? 'red-text'
                        : '';
            },
            currencyFilter(figure) {
                return Accounting.formatNumber(figure);
            },
        },
        computed: {
            getFieldValues() {
                return this.currentInterestRate + this.unformatFigure(this.currentBalance) + this.unformatFigure(this.monthlyPayment) + this.loanTermInYears + this.refinanceInterestRate + this.unformatFigure(this.loanAmount);
            },
        },
        mounted() {
            this.calculateRefinance();
        },
        watch: {
            getFieldValues() {
                this.calculateRefinance();
            },
        },
        components: {
            LtLogo,
            AdvertisingDisclosures,
            LtLabel,
        },
        mixins: [BaseMixin],
    };
</script>
<style src="../../../assets/styles/estimated-savings.scss" lang="scss"></style>
